import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import { useEntityGraphContext } from '../../Wrapper/EntityGraphProvider'

export const DisplayOptions = ({
  node,
  onUpdateNode
}) => {
  const { state } = useEntityGraphContext()
  const [localHeader, setLocalHeader] = useState(node.data?.header ?? '')
  const [localDescription, setLocalDescription] = useState(node.data?.description ?? '')

  const linkedEntity = useMemo(() => {
    if (!state.linkedEntityData || !node.data?.entityProps?.levelTypeId) return null
    return state.linkedEntityData[node.data?.entityProps?.levelTypeId]?.[node.data?.entityProps?.levelId] ?? null
  }, [state.linkedEntityData, node.data?.entityProps?.levelTypeId, node.data?.entityProps?.levelId])

  useEffect(() => {
    setLocalHeader(node.data?.header ?? '')
    setLocalDescription(node.data?.description ?? '')
  }, [node.data?.header, node.data?.description])

  const displayLabelOptions = useMemo(() => {
    const options = [
      { label: 'Custom Entry', value: 'header' }
    ]

    if (linkedEntity?.label) {
      options.push({
        label: linkedEntity.label,
        description: `This label is pulled from the linked ${node.data?.entityProps?.type ?? 'entity'}`,
        value: 'entityProps.label'
      })
    }
    options.push({ label: 'No Header', value: 'none' })

    return options
  }, [linkedEntity?.label, node.data?.entityProps?.type])

  const displayDescriptionOptions = useMemo(() => {
    const options = [
      { label: 'Custom Entry', value: 'description' }
    ]

    if (linkedEntity?.description) {
      options.push({
        label: linkedEntity?.description,
        description: `This description is pulled from the linked ${node.data?.entityProps?.type ?? 'entity'}`,
        value: 'entityProps.description'
      })
    }
    options.push({ label: 'No Description', value: 'none' })

    return options
  }, [linkedEntity?.description, node.data?.entityProps?.type])

  const imageOptions = useMemo(() => {
    const options = [
      { label: 'No Image', value: 'none' }
    ]

    if (linkedEntity?.image) {
      options.push({
        label: 'Entity Image',
        description: `This image is pulled from the linked ${node.data?.entityProps?.type ?? 'entity'}`,
        value: 'entityProps.image'
      })
    }

    return options
  }, [linkedEntity?.image, node.data?.entityProps?.type])

  const debounceRef = useRef()
  const handleDebouncedUpdate = useCallback((path, value) => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      onUpdateNode(path, value)
    }, 300)
  }, [onUpdateNode])

  return (
    <>
      <p className='m-0 font-size-sm'>Display Options</p>
      <SydLabel label='Header'>
        <div className='flex flex-col gap-sm'>
          <SydSelect
            size='sm'
            options={displayLabelOptions}
            value={node.data?.displayOptions?.header ?? 'header'}
            onChange={(e) => onUpdateNode(['data', 'displayOptions', 'header'], e.target.value)}
          />
          {(!node.data?.displayOptions?.header || node.data?.displayOptions?.header === 'header') && (
            <SydLabel label='Custom Label'>
              <SydInput
                size='sm'
                placeholder='Enter custom header'
                value={localHeader}
                onChange={(e) => {
                  setLocalHeader(e.target.value)
                  handleDebouncedUpdate(['data', 'header'], e.target.value)
                }}
              />
            </SydLabel>
          )}
        </div>
      </SydLabel>

      <SydLabel label='Description'>
        <div className='flex flex-col gap-sm mb-2'>
          <SydSelect
            size='sm'
            options={displayDescriptionOptions}
            value={node.data?.displayOptions?.description ?? 'description'}
            onChange={(e) => onUpdateNode(['data', 'displayOptions', 'description'], e.target.value)}
          />
          {(!node.data?.displayOptions?.description || node.data?.displayOptions?.description === 'description') && (
            <SydLabel label='Custom Label'>
              <SydInput
                size='sm'
                placeholder='Enter custom description'
                value={localDescription}
                onChange={(e) => {
                  setLocalDescription(e.target.value)
                  handleDebouncedUpdate(['data', 'description'], e.target.value)
                }}
              />
            </SydLabel>
          )}
        </div>
      </SydLabel>

      <SydLabel label='Image'>
        <div className='flex flex-col gap-sm mb-2'>
          <SydSelect
            size='sm'
            options={imageOptions}
            value={node.data?.displayOptions?.image ?? 'none'}
            onChange={(e) => onUpdateNode(['data', 'displayOptions', 'image'], e.target.value)}
          />
        </div>
      </SydLabel>
    </>
  )
}

DisplayOptions.propTypes = {
  onUpdateNode: PropTypes.func.isRequired,
  node: PropTypes.shape({
    data: PropTypes.shape({
      entityProps: PropTypes.shape({
        levelTypeId: PropTypes.number,
        levelId: PropTypes.string,
        type: PropTypes.string
      }),
      displayOptions: PropTypes.shape({
        header: PropTypes.string,
        description: PropTypes.string,
        image: PropTypes.string
      }),
      header: PropTypes.string,
      description: PropTypes.string
    }),
    type: PropTypes.string
  })
}
